import { useEffect, useState } from 'react';
import { nameLift } from '../../callNames';
/** The call buttons while the bar shows (toolbox/components/web/Toolbox.tsx). */
export const CALL_BAR_SELECTOR = '#new-toolbox.visible .toolbox-content-items';
/** Each tile names its pane key here, and its name tag carries NAME_ATTRIBUTE (VRSLayout.tsx). */
const PANE_ATTRIBUTE = 'data-vrs-pane';
const NAME_ATTRIBUTE = 'data-vrs-name';
/** The bar slides in over 0.3 s (css/_toolbars.scss .new-toolbox). */
export const BAR_SETTLE_MS = 350;
/**
 * Whether two measurements raise the same names by the same amounts.
 *
 * @param {Lifts} left - One measurement.
 * @param {Lifts} right - The other.
 * @returns {boolean}
 */
function sameLifts(left, right) {
    const keys = Object.keys(left);
    return keys.length === Object.keys(right).length && keys.every(key => left[key] === right[key]);
}
/**
 * How far each tile's name rises, by pane key, to sit above the call bar
 * while it shows (callNames.ts nameLift). Measured on the page: tiles move
 * with the window, the chat rail, the tile view and a dragged self-view.
 *
 * @param {RefObject<HTMLElement>} stage - The call stage.
 * @param {boolean} toolboxVisible - Whether the call bar shows.
 * @param {string} layoutKey - Changes whenever the tiles are rearranged.
 * @returns {Lifts}
 */
export default function useNameLifts(stage, toolboxVisible, layoutKey) {
    const [lifts, setLifts] = useState({});
    useEffect(() => {
        if (!toolboxVisible || typeof window === 'undefined') {
            setLifts(current => Object.keys(current).length ? {} : current);
            return;
        }
        const measure = () => {
            const bar = document.querySelector(CALL_BAR_SELECTOR)?.getBoundingClientRect();
            const next = {};
            stage.current?.querySelectorAll(`[${PANE_ATTRIBUTE}]`).forEach(pane => {
                const name = pane.querySelector(`[${NAME_ATTRIBUTE}]`);
                const frame = name?.offsetParent;
                if (!name || !frame) {
                    return;
                }
                // Where the name sits unraised: its offsets inside the frame
                // ignore the lift, which is a transform.
                const box = frame.getBoundingClientRect();
                const left = box.left + name.offsetLeft;
                const lift = nameLift({
                    bottom: box.top + name.offsetTop + name.offsetHeight,
                    left,
                    right: left + name.offsetWidth
                }, bar);
                if (lift) {
                    next[pane.getAttribute(PANE_ATTRIBUTE) || ''] = lift;
                }
            });
            setLifts(current => sameLifts(current, next) ? current : next);
        };
        measure();
        const settle = window.setTimeout(measure, BAR_SETTLE_MS);
        window.addEventListener('resize', measure);
        // A dragged self-view lands on pointerup.
        window.addEventListener('pointerup', measure);
        return () => {
            window.clearTimeout(settle);
            window.removeEventListener('resize', measure);
            window.removeEventListener('pointerup', measure);
        };
    }, [layoutKey, stage, toolboxVisible]);
    return lifts;
}
